import { trackCityScopeEvent } from './analyticsJourneys';

export type ReportSignupMode = 'public' | 'account';

export interface ReportSignupEventContext {
  reportKey?: string | null;
  source?: string | null;
  mode?: ReportSignupMode | null;
  status?: string | null;
  reason?: string | null;
  placement?: string | null;
  emailLength?: number | null;
}

const REPORTS_CATEGORY = 'journey:reports';

function cleanEventPart(value: string | null | undefined, fallback: string): string {
  const raw = (value || '').trim().toLowerCase();
  if (!raw || raw.includes('@')) return fallback;

  const cleaned = raw
    .replace(/https?:\/\//g, '')
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);

  return cleaned || fallback;
}

export function getEmailLengthBucket(length: number | null | undefined): string {
  const normalized = Math.max(0, Math.round(Number(length) || 0));
  if (normalized === 0) return 'email_empty';
  if (normalized <= 5) return 'email_len_1_5';
  if (normalized <= 12) return 'email_len_6_12';
  if (normalized <= 32) return 'email_len_13_32';
  return 'email_len_33_plus';
}

function buildMetadata(context: ReportSignupEventContext): Record<string, unknown> {
  const metadata: Record<string, unknown> = {
    journey: 'reports',
    page_type: 'report_signup',
    report_key: cleanEventPart(context.reportKey, 'unknown_report'),
    source: cleanEventPart(context.source, 'direct'),
    mode: context.mode || 'public',
  };

  if (context.status) metadata.status = cleanEventPart(context.status, 'unknown_status');
  if (context.reason) metadata.reason = cleanEventPart(context.reason, 'unknown_reason');
  if (context.placement) metadata.placement = cleanEventPart(context.placement, 'unknown_placement');
  if (typeof context.emailLength === 'number') {
    metadata.email_length_bucket = getEmailLengthBucket(context.emailLength);
  }

  return metadata;
}

export function buildReportSignupEventName(context: ReportSignupEventContext = {}): string {
  const statusPart =
    context.status ||
    context.reason ||
    context.placement ||
    (typeof context.emailLength === 'number' ? getEmailLengthBucket(context.emailLength) : null);

  return [
    cleanEventPart(context.reportKey, 'unknown_report'),
    cleanEventPart(context.mode, 'public'),
    cleanEventPart(context.source, 'direct'),
    statusPart ? cleanEventPart(statusPart, 'detail') : null,
  ]
    .filter(Boolean)
    .join(':');
}

export function trackReportSignupEvent(
  action: string,
  context: ReportSignupEventContext = {},
  value?: number,
): void {
  trackCityScopeEvent({
    category: REPORTS_CATEGORY,
    action,
    name: buildReportSignupEventName(context),
    value,
    metadata: buildMetadata(context),
  });
}

export function trackReportSignupEmailEntered(
  email: string,
  context: Omit<ReportSignupEventContext, 'emailLength'> = {},
): void {
  const trimmed = email.trim();
  if (!trimmed) return;

  trackReportSignupEvent('report_signup_email_entered', {
    ...context,
    emailLength: trimmed.length,
  });
}
